import classNames from "classnames";
import React, { useId } from "react";
import { clamp } from "../utils";

// Star outline from app/views/icons/_star.svg (viewBox 0 0 512 512).
const STAR_PATH =
  "M394,480a16,16,0,0,1-9.39-3L256,383.76,127.39,477a16,16,0,0,1-24.55-18.08L153,310.35,23,221.2A16,16,0,0,1,32,192H192.38l48.4-148.95a16,16,0,0,1,30.44,0l48.4,149H480a16,16,0,0,1,9.05,29.2L359,310.35l50.13,148.53A16,16,0,0,1,394,480Z";

interface StarRatingProps {
  rating?: number;
  max?: number;
  className?: string;
}

export function StarRating({
  max = 5,
  rating = 0,
  className,
}: StarRatingProps) {
  const clampedRating = clamp(rating, 0, max);
  const id = useId();
  const label = `Rating: ${clampedRating} out of ${max} stars`;

  return (
    <div
      className={classNames("flex text-amber-400", className)}
      role="img"
      aria-label={label}
    >
      {Array.from({ length: max }, (_, index) => (
        // Fraction of this star to fill: full for stars below the rating, empty for
        // those above it, and the fractional remainder for the one it lands on.
        <Star
          key={index}
          fill={clamp(clampedRating - index, 0, 1)}
          clipId={`${id}-${index}`}
        />
      ))}
    </div>
  );
}

function Star({ fill, clipId }: { fill: number; clipId: string }) {
  return (
    <svg viewBox="0 0 512 512" aria-hidden className="aspect-square h-full">
      {/* Empty base behind the fill so the unfilled part of a star still reads as a star. */}
      <path d={STAR_PATH} className="fill-current text-neutral-200" />
      {/* Filled portion, clipped to the fill fraction so a fractional rating shows a partial star.
          objectBoundingBox makes the clip width a 0–1 fraction of the star's own width. */}
      <clipPath id={clipId} clipPathUnits="objectBoundingBox">
        <rect x="0" y="0" width={fill} height="1" />
      </clipPath>
      <path
        d={STAR_PATH}
        className="fill-current"
        clipPath={`url(#${clipId})`}
      />
    </svg>
  );
}
